import { useState, useCallback, useMemo } from 'react';
import useColumnOrder from './useColumnOrder';
import { applyColumnOrder } from '../utils/columnLayout';

/**
 * useColumnVisibility - Reusable hook for column visibility + personalized order
 *
 * Handles column show/hide and rearrange with localStorage persistence.
 *
 * @param {Object} options
 * @param {Array} options.allColumns - All available columns with { key, label }
 * @param {Array} options.defaultColumns - Default visible column keys
 * @param {string} options.storageKey - localStorage key for persistence
 * @returns {Object} Visibility state and actions
 */
const useColumnVisibility = ({ 
  allColumns = [], 
  defaultColumns = [], 
  storageKey = null 
}) => {
  // Initialize from localStorage or defaults
  const [visibleColumns, setVisibleColumnsState] = useState(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          return new Set(JSON.parse(saved));
        }
      } catch (e) {
        console.warn('Failed to load column visibility from localStorage:', e);
      }
    }
    // Default to defaultColumns array or all column keys
    if (defaultColumns.length > 0) {
      return new Set(defaultColumns.map(c => typeof c === 'string' ? c : c.key));
    }
    return new Set(allColumns.map(c => c.key));
  });

  /**
   * Update visible columns and persist to localStorage
   */
  const setVisibleColumns = useCallback((newColumns) => {
    const newSet = newColumns instanceof Set ? newColumns : new Set(newColumns);
    setVisibleColumnsState(newSet);
    
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify([...newSet]));
      } catch (e) {
        console.warn('Failed to save column visibility to localStorage:', e);
      }
    }
  }, [storageKey]);

  /**
   * Toggle single column visibility
   */
  const toggleColumn = useCallback((columnKey) => {
    setVisibleColumnsState(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify([...newSet]));
        } catch (e) {
          console.warn('Failed to save column visibility to localStorage:', e);
        }
      }
      
      return newSet;
    });
  }, [storageKey]);

  /**
   * Show all columns
   */
  const showAll = useCallback(() => {
    const allKeys = allColumns.map(c => c.key);
    setVisibleColumns(new Set(allKeys));
  }, [allColumns, setVisibleColumns]);

  /**
   * Reset to default columns
   */
  const resetToDefault = useCallback(() => {
    const defaultKeys = defaultColumns.map(c => typeof c === 'string' ? c : c.key);
    setVisibleColumns(new Set(defaultKeys));
  }, [defaultColumns, setVisibleColumns]);

  /**
   * Check if column is visible
   */
  const isColumnVisible = useCallback((columnKey) => {
    return visibleColumns.has(columnKey);
  }, [visibleColumns]);

  const orderHook = useColumnOrder(storageKey ? `${storageKey}_order` : null, allColumns);

  /**
   * Get displayed columns (filtered by visibility, in the user's order)
   */
  const displayedColumns = useMemo(() => {
    return applyColumnOrder(allColumns, orderHook.columnOrder).filter(col => visibleColumns.has(col.key));
  }, [allColumns, visibleColumns, orderHook.columnOrder]);

  const orderedColumns = useMemo(
    () => applyColumnOrder(allColumns, orderHook.columnOrder),
    [allColumns, orderHook.columnOrder]
  );

  /**
   * Get hidden columns
   */
  const hiddenColumns = useMemo(() => {
    return allColumns.filter(col => !visibleColumns.has(col.key));
  }, [allColumns, visibleColumns]);

  return {
    // State
    visibleColumns,
    displayedColumns,
    hiddenColumns,
    orderedColumns,
    columnOrder: orderHook.columnOrder,

    // Actions
    toggleColumn,
    showAll,
    resetToDefault,
    setVisibleColumns,
    isColumnVisible,
    moveColumn: orderHook.moveColumn,
    resetOrder: orderHook.resetOrder,
  };
};

export default useColumnVisibility;
